import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Paper } from "@mui/material";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useModules, usePrograms } from "../../hooks/useRedux";
import { setModulesUploadProgress, clearModulesUploadProgress, createModule as createModuleAction } from "../../store";
import ModuleForm from "./ModuleForm";
import { Module } from "../../store/slices/types";
import { endPoints } from "../../services/endpoints";
import { API_BASE_URL } from "../../config/api";
import axios from "axios";

const AddModule: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { uploadProgress } = useModules();
  const { programs } = usePrograms();
  const [saving, setSaving] = useState(false);

  const handleSave = async (moduleData: Partial<Module>, videoFile?: File) => {
    try {
      setSaving(true);
      dispatch(clearModulesUploadProgress());

      // Create FormData with module data and video file
      const formData = new FormData();
      
      // Append all module fields
      formData.append('title', moduleData.title || "");
      formData.append('difficulty', moduleData.difficulty || "beginner");
      formData.append('content', moduleData.content || "");
      formData.append('programId', moduleData.programId || "");
      formData.append('weekNumber', String(moduleData.weekNumber || 1));
      formData.append('order', String(moduleData.order || 1));
      formData.append('duration', String(moduleData.duration || 0));
      formData.append('type', moduleData.type || "text");
      formData.append('isActive', String(moduleData.isActive !== undefined ? moduleData.isActive : true));
      formData.append('createdBy', "admin");
      formData.append('createdAt', String(Date.now()));
      formData.append('updatedAt', String(Date.now()));
      
      // Append tags as JSON string
      if (moduleData.tags && Array.isArray(moduleData.tags)) {
        formData.append('tags', JSON.stringify(moduleData.tags));
      }
      
      // Append video file if provided
      if (videoFile) {
        formData.append('video', videoFile);
      } else if (moduleData.videoUrl) {
        // If no new file but existing URL, keep the URL
        formData.append('videoUrl', moduleData.videoUrl);
      }
      
      const token = localStorage.getItem('authToken');
      
      // Single API call with progress tracking
      const response = await axios.post(
        `${API_BASE_URL}${endPoints.modules.create}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: token ? `Bearer ${token}` : '',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              dispatch(setModulesUploadProgress(percentCompleted));
            }
          },
        }
      );
      
      dispatch(clearModulesUploadProgress());
      
      if (response.data?.success || response.data) {
        const newModule = response.data?.data || response.data;
        dispatch(createModuleAction(newModule));
        toast.success("module is created");
        navigate("/modules");
      } else {
        toast.error("Failed to create module");
      }
    } catch (error: any) {
      console.error("Failed to create module:", error);
      dispatch(clearModulesUploadProgress());
      toast.error(
        `❌ Failed to create module: ${
          error?.response?.data?.message || error?.message || "Unknown error"
        }`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/modules");
  };

  return (
    <Box>
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          mb: 4,
        }}
      >
        <Typography variant='h4' fontWeight='700' gutterBottom>
          Create New Module
        </Typography>
        <Typography variant='body1' sx={{ opacity: 0.9 }}>
          Add a new learning module to your library
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <ModuleForm
          module={null}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          uploadProgress={uploadProgress}
          programs={programs}
        />
      </Paper>
    </Box>
  );
};

export default AddModule;

